import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MapPin, Star, ShieldCheck, ChevronLeft, Loader2, Wallet as WalletIcon, KeyRound, Car, ArrowRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PersonaVerify from "../components/PersonaVerify";
import VerifiedBadge from "../components/VerifiedBadge";
import { VehicleForm } from "./MyVehicles";
import StripePayment from "../components/StripePayment";
import RateOptions from "../components/RateOptions";
import DriverConsent from "../components/DriverConsent";
import LongStayOptions from "../components/LongStayOptions";
import CheckoutHoldBar from "../components/CheckoutHoldBar";
import { useHoldCountdown } from "../lib/holdTimer";
import { DateField, TimeField } from "../components/DateTimeFields";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { PageLoading, ErrorState } from "../components/States";
import StatusBadge from "../components/StatusBadge";
import { api } from "../lib/api";
import { CANCELLATION_POLICIES, CANCELLATION_PLATFORM_NOTE, policyLabel } from "../lib/cancellation";
import { useAuth } from "../context/AuthContext";
import { fmtMoney, fmtCents, fmtDuration, fmtDateTimeZone, setPageTitle, tzLabel, instantToZoneParts, SPACE_TYPE_ICONS } from "../lib/format";
import { durationLabel, fmtDayShort, fmtTime } from "../lib/scheduler";
import { fadeUp, staggerParent } from "../lib/motion";

// Listing-local wall clock, sent NAIVE — the server interprets it in the
// listing's IANA timezone. Never let the browser's timezone shift it.
const toIso = (date, time) => {
  if (!date || !time) return null;
  return `${date}T${time}:00`;
};
const toLocalDT = (date, time) => (date && time ? new Date(`${date}T${time}:00`) : null);
const _pad = (n) => String(n).padStart(2, "0");
const dstr = (d) => `${d.getFullYear()}-${_pad(d.getMonth() + 1)}-${_pad(d.getDate())}`;
const tstr = (d) => `${_pad(d.getHours())}:${_pad(Math.round(d.getMinutes() / 15) * 15 % 60)}`;

export default function SpotDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [spot, setSpot] = useState(null);
  const [error, setError] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  // Four independent booking controls — start date/time + end date/time.
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");
  const [busy, setBusy] = useState(false);
  const [bookingError, setBookingError] = useState(null);
  const [booking, setBooking] = useState(null);
  const [payment, setPayment] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportType, setReportType] = useState("owner");
  const [reportDetails, setReportDetails] = useState("");
  const [reportEmail, setReportEmail] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("");
  const [pricingMode, setPricingMode] = useState("hourly"); // driver-selected rate option — server-validated
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const [vehicleModal, setVehicleModal] = useState(false);
  const [vehBusy, setVehBusy] = useState(false);
  const [personaOpen, setPersonaOpen] = useState(false);
  // Driver consent (server-authoritative, versioned) + per-booking rules ack.
  const [consent, setConsent] = useState(null);
  const [consentOpen, setConsentOpen] = useState(false);
  const [rulesAck, setRulesAck] = useState(false);
  const [liveAvail, setLiveAvail] = useState(null); // server slot inventory for the selected interval
  const [searchParams] = useSearchParams();

  // Resume-from-expired-hold prefill (§7): BookingDetail links here with the
  // original interval so the driver keeps their selections on a fresh quote.
  // The search params carry absolute UTC instants — convert them to the
  // LISTING's timezone wall clock (never the device timezone, which would
  // silently shift the instant when the driver is in a different zone).
  // Runs once the spot (and its canonical timezone) has loaded.
  useEffect(() => {
    if (!spot) return;
    for (const [key, setD, setT] of [["start", setStartDate, setStartTime], ["end", setEndDate, setEndTime]]) {
      const raw = searchParams.get(key);
      if (!raw) continue;
      const d = new Date(raw);
      if (isNaN(d) || d <= new Date()) continue;
      const parts = instantToZoneParts(raw, spot.timezone);
      if (parts) { setD(parts.d); setT(parts.t); }
    }
  }, [spot]); // eslint-disable-line react-hooks/exhaustive-deps

  // Unauthorized-listing report (§13) — public; login optional for property owners.
  const submitReport = async (e) => {
    e.preventDefault();
    if (!reportDetails.trim()) { toast.error("Please describe the issue"); return; }
    setReportBusy(true);
    try {
      await api.reportListing({ spot_id: id, reporter_type: reportType, details: reportDetails.trim(), contact_email: reportEmail.trim() || null });
      toast.success("Thank you — our team will review this listing.");
      setReportOpen(false); setReportDetails(""); setReportEmail("");
    } catch (err) { toast.error(err.message); }
    finally { setReportBusy(false); }
  };

  useEffect(() => {
    // The ?code= param carries the sanctioned QR/property-code exception — the
    // server validates it against this exact listing's code before allowing a
    // cross-country view.
    const code = new URLSearchParams(window.location.search).get("code");
    api.getSpot(id, code).then((s) => { setSpot(s); setPageTitle(s.title); })
      .catch((e) => setError(e.message));
    api.spotReviews(id).then(setReviews).catch(() => setReviews([]));
  }, [id]);

  useEffect(() => { if (user) api.wallet().then(setWallet).catch(() => {}); }, [user]);
  // Account-level Driver declaration status (versioned) — the server is the
  // authority; a version change re-opens the requirement automatically.
  useEffect(() => { if (user) api.driverConsentStatus().then(setConsent).catch(() => {}); }, [user]);
  useEffect(() => {
    if (!user) return;
    api.myVehicles().then((vs) => {
      setVehicles(vs);
      const def = vs.find((v) => v.is_default);
      if (def) setVehicleId(def.vehicle_id);
    }).catch(() => {});
  }, [user]);

  const CUR = (spot?.currency || "usd").toUpperCase();

  // "Today" for the date pickers is the PROPERTY's local calendar day — a
  // driver in another timezone must not be offered (or denied) the wrong day.
  const listingToday = (() => {
    if (!spot?.timezone) return new Date();
    const p = instantToZoneParts(new Date().toISOString(), spot.timezone);
    return p ? new Date(`${p.d}T00:00:00`) : new Date();
  })();

  // ---- Derived range state (presentation validation only; server is final) ----
  const startDT = toLocalDT(startDate, startTime);
  const endDT = toLocalDT(endDate, endTime);
  const dateOrderError = startDate && endDate && endDate < startDate
    ? "End date is before the start date — choose a later day."
    : null;
  const rangeError = !dateOrderError && startDT && endDT && endDT <= startDT
    ? "End time must be after the start time."
    : null;
  const rangeValid = !!(startDT && endDT && endDT > startDT);

  // Server-authoritative quote — the SAME engine that builds the PaymentIntent.
  // The client never computes amounts.
  const [quote, setQuote] = useState(null);
  const [rateOptions, setRateOptions] = useState(null); // persists across a failed quote (PRD §3)
  useEffect(() => {
    if (!spot || !rangeValid) { setQuote(null); setQuoteError(null); return; }
    const s = toIso(startDate, startTime), e = toIso(endDate, endTime);
    if (!s || !e) { setQuote(null); setQuoteError(null); return; }
    let live = true;
    setQuoteBusy(true);
    // Intervals beyond the 30-day monthly maximum: fetch the DEFAULT quote so
    // the response carries long_term_options (§4) — a forced "monthly" mode
    // would 400 before the options are computed.
    const hrs = (endDT - startDT) / 3600e3;
    const effMode = pricingMode === "monthly" && hrs > 720 ? null : pricingMode;
    api.pricingQuote(id, s, e, !!user, effMode)
      .then((q) => { if (live) { setQuote(q); setQuoteError(null); setRateOptions(q.rate_options || null); setLiveAvail(q.availability?.available !== false); } })
      .catch((err) => {
        if (!live) return;
        setQuote(null); setQuoteError(err.message);
        // The selected plan may be invalid for this interval — refresh option
        // availability anyway so disabled cards stay visible with their reason
        // (never hide the pricing options on a 400).
        api.pricingQuote(id, s, e, false, null)
          .then((q2) => { if (live) { setRateOptions(q2.rate_options || null); setLiveAvail(q2.availability?.available !== false); } })
          .catch(() => {});
      })
      .finally(() => { if (live) setQuoteBusy(false); });
    setRulesAck(false); // any material change (time/plan) requires re-acknowledgment
    return () => { live = false; };
  }, [spot, startDate, startTime, endDate, endTime, id, user, pricingMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live availability refresh (§5): re-check the server-held slot inventory
  // while the driver is deciding — a stale screen must never look bookable.
  useEffect(() => {
    if (!spot || !rangeValid) return undefined;
    const t = setInterval(() => {
      const s = toIso(startDate, startTime), e = toIso(endDate, endTime);
      if (!s || !e) return;
      api.pricingQuote(id, s, e, false, null)
        .then((q) => setLiveAvail(q.availability?.available !== false))
        .catch(() => {});
    }, 45000);
    return () => clearInterval(t);
  }, [spot, rangeValid, startDate, startTime, endDate, endTime, id]);

  // Plan-driven date controls (§2): each rate plan drives the date/time inputs.
  // Valid existing selections are preserved; only incompatible values adjust.
  const selectPlan = (mode) => {
    setPricingMode(mode);
    const s = toLocalDT(startDate, startTime);
    if (!s) return;
    if (mode === "flat" && quote?.flat_rate) {
      // Flat rate covers the host's whole window — the end is derived.
      const e = new Date(s.getTime() + quote.flat_rate.duration_hours * 3600e3);
      setEndDate(dstr(e)); setEndTime(tstr(e));
    } else if (mode === "monthly") {
      const maxE = new Date(s.getTime() + 30 * 86400e3);
      const e = toLocalDT(endDate, endTime);
      if (!e || e <= s || e > maxE) { setEndDate(dstr(maxE)); setEndTime(tstr(maxE)); }
    }
  };

  const [coupon, setCoupon] = useState("");
  const [couponApplied, setCouponApplied] = useState(null); // server-validated only
  const [startAvail, setStartAvail] = useState(null); // hhmm → available|held|booked (server)
  const [endAvail, setEndAvail] = useState(null);
  const [minimized, setMinimized] = useState(false); // floating hold bar (Part 3)

  // Server availability grid (Part 4/6): slot states for the selected day come
  // from GET /api/spots/{id}/availability — never inferred client-side.
  const fetchDayAvail = useCallback(async (dateStr, setter) => {
    if (!dateStr) { setter(null); return; }
    const d = new Date(`${dateStr}T00:00:00`);
    d.setDate(d.getDate() + 1);
    const nxt = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    try {
      const r = await api.spotAvailability(id, `${dateStr}T00:00:00`, `${nxt}T00:00:00`);
      const map = {};
      (r.slots || []).forEach((s) => { map[s.start.slice(11, 16)] = s.status; });
      setter(map);
    } catch { setter(null); }
  }, [id]);
  useEffect(() => { fetchDayAvail(startDate, setStartAvail); }, [startDate, fetchDayAvail]);
  useEffect(() => { fetchDayAvail(endDate, setEndAvail); }, [endDate, fetchDayAvail]);

  const book = async (consentJustAccepted = false) => {
    if (!user) { navigate("/login", { state: { from: `/spot/${id}` } }); return; }
    if (!vehicleId) { toast.error("Select a vehicle for this booking — add one under My Vehicles."); return; }
    const sIso = toIso(startDate, startTime), eIso = toIso(endDate, endTime);
    if (!sIso || !eIso) { toast.error("Choose a start and end date/time"); return; }
    // Account-level Driver declaration — required once per declaration version.
    if (!consentJustAccepted && consent && !consent.accepted) { setConsentOpen(true); return; }
    setBusy(true);
    setBookingError(null);
    try {
      const b = await api.createBooking({ spot_id: id, start_time: sIso, end_time: eIso, ...(vehicleId ? { vehicle_id: vehicleId } : {}), ...(pricingMode ? { pricing_mode: pricingMode } : {}), booking_rules_acknowledged: true, ...(quote?.promotion ? { promotion_id: quote.promotion.promotion_id } : {}), ...(couponApplied?.valid ? { promotion_code: couponApplied.code } : {}) });
      setBooking(b);
      const p = await api.checkoutWithOtp(b.booking_id);
      if (p.already) { navigate(`/bookings/${b.booking_id}`); return; }
      setPayment(p);
    } catch (e) {
      // Driver declaration gate: the server requires acceptance at the current
      // declaration version before any hold/payment is created.
      if (e.code === "DRIVER_CONSENT_REQUIRED") { setConsentOpen(true); return; }
      // Government-ID verification gate (Persona): the server requires an
      // approved verification before any hold/payment is created.
      if (e.status === 403 && e.message === "IDENTITY_VERIFICATION_REQUIRED") {
        setPersonaOpen(true);
        return;
      }
      setBookingError(e.message);
      toast.error(e.message);
      setBooking(null);
      // Part 7: the server revalidated and rejected — refresh the grid so the
      // driver immediately sees the slot's true (held/booked) state.
      fetchDayAvail(startDate, setStartAvail);
      fetchDayAvail(endDate, setEndAvail);
    } finally { setBusy(false); }
  };

  // Hold countdown (Part 2/A4): shared server-authoritative timer — driven by
  // hold_expires_at + server_now only. On expiry the stale checkout is
  // invalidated and the driver returns to the booking flow.
  const holdLeft = useHoldCountdown(booking, () => {
    setBooking(null);
    setPayment(null);
    setQuote(null);
    toast.error("This spot is no longer held for you. Please select an available time again.", { duration: 6000 });
  });

  const applyCoupon = async () => {
    const sIso = toIso(startDate, startTime), eIso = toIso(endDate, endTime);
    if (!sIso || !eIso) { toast.error("Choose a start and end date/time first"); return; }
    if (!coupon.trim()) return;
    try {
      const r = await api.validateCoupon({ code: coupon.trim(), spot_id: id, start: sIso, end: eIso });
      if (r.valid) {
        setCouponApplied(r);
        toast.success(`Promo ${r.code} applied — ${r.discount_percent}% off the parking fare`);
      } else {
        setCouponApplied(null);
        toast.error(r.reason || "This promo code is not valid");
      }
    } catch (e) { setCouponApplied(null); toast.error(e.message); }
  };

  const payWithWallet = async () => {
    if (!booking) return;
    setBusy(true);
    try {
      await api.walletPayBooking(booking.booking_id);
      toast.success("Paid from wallet — your arrival code is ready");
      navigate(`/bookings/${booking.booking_id}`);
    } catch (e) {
      if (e.status === 403 && e.message === "IDENTITY_VERIFICATION_REQUIRED") { setPersonaOpen(true); return; }
      toast.error(e.message);
    } finally { setBusy(false); }
  };

  // Pricing selection happens BEFORE payment only — once a quote is confirmed
  // and the booking hold exists, the payment step is read-only (PRD §7).

  const reloadVehicles = async (selectId) => {
    try {
      const vs = await api.myVehicles();
      setVehicles(vs);
      if (selectId) setVehicleId(selectId);
    } catch { /* keep the existing list */ }
  };

  const vehiclesById = useMemo(() => Object.fromEntries(vehicles.map((v) => [v.vehicle_id, v])), [vehicles]);
  const selectedVehicle = vehicleId ? vehiclesById[vehicleId] : null;

  if (error) return <Shell><ErrorState body={error} onRetry={() => window.location.reload()} /></Shell>;
  if (!spot) return <Shell><PageLoading label="Loading parking space…" /></Shell>;

  const images = spot.images || [];
  const canWalletPay = booking && wallet && wallet.balance_cents >= Math.round(booking.total * 100);
  const availabilitySummary = `${spot.available_days ? `${spot.available_days.length} day(s) per week` : "Every day"}${spot.available_start_hour != null ? ` · ${spot.available_start_hour}:00–${spot.available_end_hour}:00` : ""}`;

  return (
    <Shell>
      <div data-testid="spot-detail-page">
        <Link to="/find" className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.14em] text-zinc-500 hover:text-white transition-colors" data-testid="back-to-search">
          <ChevronLeft className="w-4 h-4" /> Back to search
        </Link>

        <motion.div variants={staggerParent(0.06)} initial="hidden" animate="show" className="mt-7 grid lg:grid-cols-12 gap-10">
          {/* ================= LEFT — spot / host / details ================= */}
          <div className="lg:col-span-7">
            <motion.div variants={fadeUp} className="rounded-2xl overflow-hidden border border-[#27272A] bg-[#050607] h-72 sm:h-[26rem] relative shadow-2xl shadow-black/40">
              {images.length ? (
                <img src={images[imgIdx]} alt={spot.title} className="w-full h-full object-cover" data-testid="spot-hero-image" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-8xl opacity-50" aria-hidden="true">{SPACE_TYPE_ICONS[spot.space_type] || "🅿️"}</div>
              )}
              <span className="absolute top-4 left-4 text-[10px] font-semibold uppercase tracking-[0.14em] px-2.5 py-1 rounded-md bg-black/60 backdrop-blur-md border border-white/10 text-zinc-200">{spot.space_type}</span>
            </motion.div>
            {images.length > 1 && (
              <motion.div variants={fadeUp} className="mt-3 flex gap-2 overflow-auto" data-testid="spot-gallery">
                {images.map((im, i) => (
                  <button key={i} onClick={() => setImgIdx(i)} aria-label={`Photo ${i + 1}`}
                          className={`w-20 h-14 rounded-lg overflow-hidden border transition-colors ${i === imgIdx ? "border-[#E50914]" : "border-[#27272A] hover:border-[#3F3F46]"}`}>
                    <img src={im} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </motion.div>
            )}

            <motion.div variants={fadeUp} className="mt-9">
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                <span className="meta-label">{spot.space_type} space</span>
                {spot.rating != null ? (
                  <span className="flex items-center gap-1.5 text-sm text-amber-400" data-testid="spot-rating">
                    <Star className="w-4 h-4 fill-amber-400" aria-hidden="true" />{spot.rating} · {spot.review_count} review{spot.review_count === 1 ? "" : "s"}
                  </span>
                ) : (
                  <span className="badge-red" data-testid="spot-no-reviews">New — no reviews yet</span>
                )}
              </div>
              <h1 className="type-display mt-3" data-testid="spot-title">{spot.title}</h1>
              <p className="mt-3 text-sm text-zinc-500 flex items-center gap-1.5"><MapPin className="w-4 h-4 shrink-0" />{spot.address}</p>
              {spot.property_code && (
                <p className="mt-2 text-xs text-zinc-500 flex items-center gap-1.5" data-testid="spot-property-code">
                  <KeyRound className="w-3.5 h-3.5 text-[#FF3B45] shrink-0" /> Property code <span className="font-mono font-bold tracking-[0.18em] text-white">{spot.property_code}</span>
                </p>
              )}
              {spot.approximate_location && <p className="mt-1.5 text-xs text-zinc-600" data-testid="spot-approx-note">Approximate area — the exact address is shared once your booking payment is authorized.</p>}
            </motion.div>

            {/* Host — real photo, name, trust info the API already provides */}
            <motion.div variants={fadeUp} className="mt-8 surface-flat p-5 flex items-center gap-4" data-testid="host-card">
              {spot.host_picture ? (
                <img src={spot.host_picture} alt={`${spot.host_name}'s photo`} className="w-12 h-12 rounded-full object-cover shrink-0 ring-1 ring-[#27272A]" data-testid="host-card-photo" />
              ) : (
                <span className="w-12 h-12 rounded-full bg-[#E50914]/15 text-[#FF3B45] flex items-center justify-center text-base font-bold font-display shrink-0" data-testid="host-card-initial">{(spot.host_name || "H")[0].toUpperCase()}</span>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold flex items-center gap-2 flex-wrap" data-testid="spot-host-name">Hosted by {spot.host_name}<VerifiedBadge verified={spot.host_id_verified} testId="spot-host-id-verified" /></p>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-500">
                  {spot.rating != null && (
                    <span className="flex items-center gap-1 text-amber-400"><Star className="w-3 h-3 fill-amber-400" aria-hidden="true" />{spot.rating} · {spot.review_count} review{spot.review_count === 1 ? "" : "s"}</span>
                  )}
                  {spot.trust?.location_proximity_verified && (
                    <span className="flex items-center gap-1 text-emerald-400"><ShieldCheck className="w-3 h-3" /> Location verified</span>
                  )}
                  {!spot.trust?.location_proximity_verified && spot.trust?.host_attestation && (
                    <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Host attestation on file</span>
                  )}
                </div>
                <p className="mt-1.5 text-[11px] text-zinc-600 flex items-center gap-1.5"><KeyRound className="w-3 h-3 shrink-0" /> Access instructions unlock after booking confirmation</p>
              </div>
            </motion.div>

            {/* Compact scannable sections */}
            <motion.div variants={fadeUp} className="mt-8" data-testid="spot-details-accordions">
              <Accordion type="multiple" defaultValue={["about", "details"]} className="divide-y divide-[#1F2328] border-y border-[#1F2328]">
                <AccordionItem value="about" className="border-none">
                  <AccordionTrigger className="text-sm font-semibold text-zinc-100 hover:no-underline py-4" data-testid="accordion-about">About this spot</AccordionTrigger>
                  <AccordionContent>
                    <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-line pb-2" data-testid="spot-description">{spot.description}</p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="details" className="border-none">
                  <AccordionTrigger className="text-sm font-semibold text-zinc-100 hover:no-underline py-4" data-testid="accordion-details">Parking details</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pb-2 text-sm">
                      <div className="flex justify-between gap-4"><span className="text-zinc-500">Parking type</span><span className="text-zinc-200 font-medium text-right">{spot.space_type}</span></div>
                      <div className="flex justify-between gap-4"><span className="text-zinc-500">Availability</span><span className="text-zinc-200 font-medium text-right" data-testid="availability-summary">{availabilitySummary}</span></div>
                      <div className="flex justify-between gap-4">
                        <span className="text-zinc-500">Vehicle fit</span>
                        <span className="text-zinc-200 font-medium text-right" data-testid="vehicle-fit">
                          {spot.max_vehicle_height_m ? `Max height ${spot.max_vehicle_height_m} m` : "Max height: Not specified"}
                          {spot.vehicle_restrictions ? ` · ${spot.vehicle_restrictions}` : ""}
                        </span>
                      </div>
                      <div className="flex justify-between gap-4"><span className="text-zinc-500">Arrival</span><span className="text-zinc-200 font-medium text-right">Verified arrival — one-time code before you're charged</span></div>
                      {spot.booking_policy === "limited" && spot.max_duration_hours && (
                        <div className="flex justify-between gap-4"><span className="text-zinc-500">Duration limit</span><span className="text-zinc-200 font-medium text-right" data-testid="duration-limit">Up to {spot.max_duration_hours} hours per booking</span></div>
                      )}
                      {spot.amenities?.length > 0 && (
                        <div>
                          <p className="text-zinc-500 mb-2">Features</p>
                          <div className="flex flex-wrap gap-2">
                            {spot.amenities.map((a) => <span key={a} className="text-xs px-3 py-1.5 rounded-lg bg-[#0D1114] border border-[#27272A] text-zinc-300">{a}</span>)}
                          </div>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="cancellation" className="border-none">
                  <AccordionTrigger className="text-sm font-semibold text-zinc-100 hover:no-underline py-4" data-testid="accordion-cancellation">Cancellation policy</AccordionTrigger>
                  <AccordionContent>
                    <div className="pb-2" data-testid="cancellation-policy-section">
                      <p className="text-sm font-semibold flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-[#FF3B45] shrink-0" />
                        {policyLabel(spot.cancellation_policy) || "Not specified by host"}
                      </p>
                      <p className="mt-1.5 text-xs text-zinc-500 leading-relaxed">
                        {CANCELLATION_POLICIES.find((p) => p.value === spot.cancellation_policy)?.driverText || "The host has not stated a cancellation preference for this listing."}
                      </p>
                      <p className="mt-3 pt-3 border-t border-[#18181B] text-[11px] text-zinc-600 leading-relaxed">{CANCELLATION_PLATFORM_NOTE}</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {spot.trust && (
                  <AccordionItem value="trust" className="border-none">
                    <AccordionTrigger className="text-sm font-semibold text-zinc-100 hover:no-underline py-4" data-testid="accordion-trust">Trust &amp; safety</AccordionTrigger>
                    <AccordionContent>
                      <div className="pb-2" data-testid="spot-trust-block">
                        <div className="flex flex-wrap gap-x-5 gap-y-2">
                          {spot.trust.location_proximity_verified && (
                            <span className="flex items-center gap-1.5 text-xs text-emerald-400" data-testid="trust-proximity"><ShieldCheck className="w-3.5 h-3.5" /> Location verified by host visit</span>
                          )}
                          {spot.trust.location_confirmed_by_host && (
                            <span className="flex items-center gap-1.5 text-xs text-zinc-300" data-testid="trust-location-confirmed"><ShieldCheck className="w-3.5 h-3.5" /> Location confirmed by host</span>
                          )}
                          {spot.trust.host_attestation && (
                            <span className="flex items-center gap-1.5 text-xs text-zinc-300" data-testid="trust-attestation"><ShieldCheck className="w-3.5 h-3.5" /> Host attestation on file</span>
                          )}
                          {spot.trust.authorization_type && (
                            <span className="text-xs text-zinc-500" data-testid="trust-authorization">Authorized as: {({ own: "property owner", rent: "tenant/lessee", manage: "property manager", permission: "with owner's permission", business: "business/property manager" })[spot.trust.authorization_type] || spot.trust.authorization_type}</span>
                          )}
                        </div>
                        <button onClick={() => setReportOpen((v) => !v)} data-testid="report-listing-btn"
                                className="mt-3 text-xs text-zinc-600 hover:text-zinc-300 underline underline-offset-2 transition-colors">
                          Report unauthorized listing
                        </button>
                        {reportOpen && (
                          <form className="mt-4 space-y-3 border-t border-[#1F2328] pt-4" data-testid="report-form" onSubmit={submitReport}>
                            <p className="text-xs text-zinc-500 leading-relaxed">Own or manage this property? Tell us — our team reviews every report. The listing is placed under neutral review while we investigate.</p>
                            <select value={reportType} onChange={(e) => setReportType(e.target.value)} data-testid="report-type" className="input-dark" aria-label="I am the">
                              <option value="owner">I own this property</option>
                              <option value="tenant">I rent/live at this property</option>
                              <option value="manager">I manage this property</option>
                              <option value="driver">I'm a driver</option>
                              <option value="other">Other</option>
                            </select>
                            <textarea value={reportDetails} onChange={(e) => setReportDetails(e.target.value)} data-testid="report-details" className="input-dark min-h-20" placeholder="Describe the issue…" aria-label="Report details" />
                            {!user && <input type="email" value={reportEmail} onChange={(e) => setReportEmail(e.target.value)} data-testid="report-email" className="input-dark" placeholder="Contact email (optional)" aria-label="Contact email" />}
                            <button type="submit" disabled={reportBusy} data-testid="report-submit-btn" className="btn-secondary text-xs !px-4 !py-2">
                              {reportBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Submit report
                            </button>
                          </form>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )}
              </Accordion>
            </motion.div>
          </div>

          {/* ================= RIGHT — sticky reservation card ================= */}
          <div className="lg:col-span-5">
            <motion.div variants={fadeUp} className="rounded-2xl border border-[#27272A] bg-[#0D1114]/80 backdrop-blur-sm p-6 sm:p-7 lg:sticky lg:top-28 shadow-2xl shadow-black/40" data-testid="booking-panel">
              <div className="flex items-baseline gap-2">
                <p className="font-display font-bold text-3xl tracking-tight">{fmtMoney(spot.price_per_hour, CUR)}</p>
                <span className="text-sm text-zinc-500">/ hour</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs text-zinc-400">
                {spot.price_6h && <span className="px-2.5 py-1 rounded-md bg-[#080B0D] border border-[#27272A]">6h · {fmtMoney(spot.price_6h, CUR)}</span>}
                {spot.price_12h && <span className="px-2.5 py-1 rounded-md bg-[#080B0D] border border-[#27272A]">12h · {fmtMoney(spot.price_12h, CUR)}</span>}
                {spot.price_24h && <span className="px-2.5 py-1 rounded-md bg-[#080B0D] border border-[#27272A]">24h · {fmtMoney(spot.price_24h, CUR)}</span>}
                {spot.monthly_enabled && spot.monthly_price && <span className="px-2.5 py-1 rounded-md bg-[#080B0D] border border-[#27272A]">Monthly · {fmtMoney(spot.monthly_price, CUR)}</span>}
              </div>

              {/* Compact host row — real photo, real API fields only */}
              <div className="mt-5 flex items-center gap-3 rounded-xl border border-[#1F2328] bg-[#0A0D10] p-3" data-testid="host-row">
                {spot.host_picture ? (
                  <img src={spot.host_picture} alt={`${spot.host_name}'s photo`} className="w-9 h-9 rounded-full object-cover shrink-0" data-testid="host-row-photo" />
                ) : (
                  <span className="w-9 h-9 rounded-full bg-[#E50914]/15 text-[#FF3B45] flex items-center justify-center text-xs font-bold font-display shrink-0">{(spot.host_name || "H")[0].toUpperCase()}</span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate" data-testid="host-row-name">{spot.host_name}</p>
                  <p className="text-[11px] text-zinc-500 truncate">
                    {spot.rating != null ? `Rated ${spot.rating} · ${spot.review_count} review${spot.review_count === 1 ? "" : "s"}` : "No reviews yet"}
                    {spot.trust?.location_proximity_verified ? " · Location verified" : ""}
                  </p>
                </div>
              </div>

              {!payment ? (
                <>
                  {/* WHEN — four independent controls, no walls of pills */}
                  <div className="mt-6">
                    <p className="meta-label mb-3">When do you need it?</p>
                    {spot?.timezone && (
                      <p className="mb-3 -mt-1 text-[11px] text-zinc-500" data-testid="booking-timezone-label">
                        All times are in the property's local timezone — <span className="text-zinc-300 font-medium">{tzLabel(spot.timezone)}</span>
                      </p>
                    )}
                    <div className="space-y-2.5">
                      <div className="grid grid-cols-2 gap-2.5">
                        <DateField label="Start date" value={startDate} onChange={setStartDate} minDate={listingToday} rangeStart={startDate} rangeEnd={endDate} testId="start-date-field" />
                        <TimeField label="Start time" value={startTime} onChange={setStartTime} testId="start-time-field" availability={startAvail} />
                      </div>
                      {pricingMode === "flat" && quote?.flat_rate ? (
                        // Flat rate: the end is derived from the host's configured
                        // window — shown read-only, never a second guess (§2/§5).
                        <div className="rounded-xl border border-[#27272A] bg-[#0A0D10] px-3.5 py-3" data-testid="flat-covered-until">
                          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">Covered until</p>
                          <p className="mt-1 text-sm font-bold text-white">{endDT ? `${fmtDayShort(endDT)}, ${fmtTime(endDT)}` : "—"}</p>
                          <p className="mt-0.5 text-[11px] text-zinc-600">The flat rate covers the host's full {quote.flat_rate.duration_hours}-hour window from your start.</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-2.5">
                          <DateField label="End date" value={endDate} onChange={setEndDate} minDate={listingToday} rangeStart={startDate} rangeEnd={endDate} invalid={!!dateOrderError} testId="end-date-field" />
                          <TimeField label="End time" value={endTime} onChange={setEndTime} invalid={!!rangeError} testId="end-time-field" availability={endAvail} />
                        </div>
                      )}
                      {pricingMode === "monthly" && (
                        <p className="text-[11px] text-zinc-500 leading-relaxed" data-testid="monthly-hint">
                          Monthly covers up to 30 days from your start — the fixed monthly rate applies to the whole period you choose within that limit.
                        </p>
                      )}
                    </div>
                    {(dateOrderError || rangeError) && (
                      <p className="mt-2.5 text-xs font-medium text-red-400" data-testid="range-error" role="alert">{dateOrderError || rangeError}</p>
                    )}
                    {rangeValid && (
                      <div className="mt-3.5 rounded-xl border border-[#1F2328] bg-[#0A0D10] p-3.5" data-testid="reservation-summary" aria-live="polite">
                        <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-[#FF3B45]">Your reservation</p>
                        <p className="mt-1.5 text-sm font-bold text-white" data-testid="reservation-range">
                          {fmtDayShort(startDT)}, {fmtTime(startDT)} <ArrowRight className="inline w-3.5 h-3.5 text-zinc-600 -mt-0.5" aria-hidden="true" /> {fmtDayShort(endDT)}, {fmtTime(endDT)}
                        </p>
                        <p className="mt-0.5 text-xs text-zinc-500" data-testid="reservation-duration">{durationLabel(startDT, endDT)}</p>
                      </div>
                    )}
                  </div>

                  {/* VEHICLE — existing selection system, unchanged */}
                  {user && (
                    <div className="mt-6">
                      <p className="meta-label mb-3">Vehicle <span className="text-[#FF3B45]">*</span></p>
                      {vehicles.length > 0 ? (
                        <>
                          <div className="flex items-center gap-3 rounded-xl border border-[#27272A] bg-[#0A0D10] p-3" data-testid="vehicle-current">
                            <span className="w-9 h-9 rounded-lg bg-[#1F2328] flex items-center justify-center shrink-0"><Car className="w-4 h-4 text-zinc-300" /></span>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold truncate" data-testid="vehicle-current-name">
                                {selectedVehicle ? `${selectedVehicle.nickname}${selectedVehicle.make ? ` · ${selectedVehicle.make} ${selectedVehicle.model || ""}` : ""}` : "Select a vehicle"}
                              </p>
                              <p className="text-[11px] text-zinc-500" data-testid="vehicle-current-plate">{selectedVehicle ? selectedVehicle.plate : "Required — the host verifies your plate at arrival"}</p>
                            </div>
                          </div>
                          <label htmlFor="bk-vehicle" className="sr-only">Change vehicle</label>
                          <select id="bk-vehicle" data-testid="booking-vehicle-select" value={vehicleId}
                                  onChange={(e) => setVehicleId(e.target.value)} className="input-dark mt-2.5">
                            <option value="">Change vehicle…</option>
                            {vehicles.map((v) => (
                              <option key={v.vehicle_id} value={v.vehicle_id}>
                                {`${v.nickname} · ${v.make} ${v.model} · ${v.plate}`}
                              </option>
                            ))}
                          </select>
                          <p className="mt-1.5 text-[11px] text-zinc-600">Your plate is shared with the host only after payment is authorized — the host verifies it at arrival.</p>
                          <button type="button" onClick={() => setVehicleModal(true)} data-testid="booking-add-another-vehicle"
                                  className="mt-2 text-[11px] font-semibold text-zinc-500 hover:text-white transition-colors">
                            + Add a new vehicle
                          </button>
                        </>
                      ) : (
                        <div className="p-4 rounded-xl border border-[#27272A] bg-[#0D1114]/60" data-testid="booking-vehicle-required">
                          <p className="text-sm text-zinc-300">A vehicle is required to book — the host verifies your license plate at arrival.</p>
                          <button type="button" onClick={() => setVehicleModal(true)} data-testid="booking-add-vehicle" className="btn-secondary !px-4 !py-2 text-xs mt-3 inline-flex items-center gap-1.5">
                            <Car className="w-3.5 h-3.5" /> Add a vehicle
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* PRICING OPTIONS — every Host-configured plan as a card;
                      unavailable options stay visible with the server's reason.
                      The quote (and the PaymentIntent) comes from the same
                      server engine — the client never computes amounts. */}
                  {rateOptions && (
                    <div className="mt-5">
                      <RateOptions options={rateOptions} currency={CUR} busy={quoteBusy}
                                   selected={pricingMode} onSelect={selectPlan} />
                    </div>
                  )}

                  {/* Long-stay selection (§4) — only for intervals beyond the
                      30-day monthly maximum; every option is server-computed. */}
                  {quote?.long_term_options && (
                    <LongStayOptions options={quote.long_term_options} currency={CUR} spotId={id}
                                     monthlyPlusSelected={pricingMode === "monthly_plus"}
                                     onMonthlyPlus={() => setPricingMode("monthly_plus")}
                                     onShorten={() => {
                                       const s = toLocalDT(startDate, startTime);
                                       if (!s) return;
                                       const e = new Date(s.getTime() + 30 * 86400e3);
                                       setPricingMode("monthly");
                                       setEndDate(dstr(e)); setEndTime(tstr(e));
                                     }} />
                  )}

                  {/* Live availability (§5) — from the server's slot inventory,
                      refreshed after selection changes and on a timer. */}
                  {liveAvail === false && (
                    <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3.5 text-xs text-amber-300 leading-relaxed"
                         data-testid="availability-conflict" role="alert">
                      This space is already booked or held for part of your selected time — adjust your dates or times to continue.
                    </div>
                  )}

                  {/* PRICE — every value straight from the server quote (Part 3/4:
                      no percentages, no client math, Platform Fee = service fee +
                      estimated payment processing) */}
                  {quote && (
                    <div className="mt-6 pt-5 border-t border-[#1F2328] space-y-2.5 text-sm" data-testid="price-breakdown">
                      <div className="flex justify-between text-zinc-400"><span>Base fare <span className="text-zinc-600">· {fmtDuration(`${startDate}T${startTime}`, `${endDate}T${endTime}`)} · {quote.pricing_tier} rate</span></span><span data-testid="price-parking">{fmtMoney(quote.subtotal_cents / 100, CUR)}</span></div>
                      {quote.discount_cents > 0 && (
                        <div className="flex justify-between text-emerald-400"><span>{quote.promotion?.type === "coupon" ? `Promo ${couponApplied?.code || ""}`.trim() : quote.promotion?.type === "referral_reward" ? "Referral discount" : "New-user reward"} ({quote.discount_percent}%)</span><span data-testid="price-discount">-{fmtMoney(quote.discount_cents / 100, CUR)}</span></div>
                      )}
                      <div className="flex justify-between text-zinc-400"><span>Service fee</span><span data-testid="price-fee">{fmtMoney((quote.platform_fee_cents + (quote.payment_handling_cents || 0)) / 100, CUR)}</span></div>
                      <details className="text-[11px]" data-testid="service-fee-info">
                        <summary className="cursor-pointer select-none text-zinc-500 hover:text-zinc-300 underline decoration-dotted underline-offset-2">What does the service fee include?</summary>
                        <div className="mt-1.5 space-y-1" data-testid="service-fee-explainer">
                          <p className="flex justify-between text-zinc-500"><span>Platform fee</span><span data-testid="price-fee-platform">{fmtMoney(quote.platform_fee_cents / 100, CUR)}</span></p>
                          <p className="flex justify-between text-zinc-500"><span>Payment processing fee</span><span data-testid="price-fee-processing">{fmtMoney((quote.payment_handling_cents || 0) / 100, CUR)}</span></p>
                          <p className="text-zinc-500 leading-relaxed pt-1">The service fee is charged by MyRedPark for facilitating bookings and providing the platform, including estimated payment processing costs. The applicable amount is shown before payment. It is separate from any taxes.</p>
                        </div>
                      </details>
                      <div className="flex justify-between text-zinc-400"><span>Tax{quote.tax_label && quote.tax_state === "calculated" ? ` (${quote.tax_label})` : ""}</span><span data-testid="price-tax">{quote.tax_state === "calculated" ? fmtMoney(quote.tax_amount ?? (quote.tax_cents || 0) / 100, CUR) : quote.tax_state === "location_missing" ? "Unavailable — listing address incomplete" : "Calculated at confirmation"}</span></div>
                      <div className="flex justify-between font-display font-bold text-base pt-1.5"><span>Total</span><span data-testid="price-total">{quoteBusy ? "…" : fmtMoney(quote.total_cents / 100, CUR)}</span></div>
                      <p className="text-[11px] text-zinc-600 leading-relaxed pt-1">{quote.tax_state === "calculated" ? `Taxes calculated for this listing's location (${quote.tax_label || "Tax"}).` : "Taxes, where legally applicable, are calculated at payment confirmation."} The service fee includes the platform fee and estimated payment processing, finalized at charge.</p>
                    </div>
                  )}

                  {rangeValid && quoteError && (
                    <p className="mt-2.5 text-xs font-medium text-red-400" data-testid="quote-error" role="alert">{quoteError}</p>
                  )}
                  {/* Coupon code — validated server-side, nothing computed locally */}
                  {user && !couponApplied?.valid && (
                    <div className="mt-4 flex gap-2" data-testid="coupon-row">
                      <input data-testid="coupon-input" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                             placeholder="Promo code" aria-label="Promo code" className="input-dark !h-10 text-sm flex-1 uppercase" />
                      <button type="button" onClick={applyCoupon} data-testid="coupon-apply-btn" className="btn-secondary !h-10 !px-4 text-xs">Apply</button>
                    </div>
                  )}
                  {couponApplied?.valid && (
                    <div className="mt-4 flex items-center justify-between rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2" data-testid="coupon-applied">
                      <span className="text-xs text-emerald-400">Promo {couponApplied.code} — {couponApplied.discount_percent}% off the parking fare</span>
                      <button type="button" onClick={() => { setCouponApplied(null); setCoupon(""); }} data-testid="coupon-remove" className="text-zinc-500 hover:text-zinc-300 text-xs">Remove</button>
                    </div>
                  )}

                  <p className="mt-5 text-xs text-zinc-500" data-testid="booking-policy-line">
                    Cancellation: <span className="text-zinc-300 font-medium">{policyLabel(spot.cancellation_policy) || "Not specified"}</span> — free before payment authorization; tiered refunds after (up to 75% of the base fare)
                  </p>

                  {/* Property-specific booking rules (PRD §13) — only rules the
                      host actually configured; acknowledgment is required by the
                      booking API on every booking. */}
                  {user && rangeValid && (
                    <div className="mt-4 rounded-xl border border-[#27272A] bg-[#0A0D10] p-4" data-testid="booking-rules">
                      <p className="meta-label mb-2">Rules for this booking</p>
                      <ul className="space-y-1.5 text-xs text-zinc-400">
                        {spot.cancellation_policy && <li>· Cancellation: {policyLabel(spot.cancellation_policy)} — free before payment authorization; tiered refunds after</li>}
                        {spot.booking_policy === "limited" && spot.max_duration_hours && <li>· Maximum parking duration: {spot.max_duration_hours} hours per booking</li>}
                        {spot.vehicle_restrictions && <li>· Vehicle restrictions: {spot.vehicle_restrictions}</li>}
                        {spot.max_vehicle_height_m && <li>· Maximum vehicle height: {spot.max_vehicle_height_m} m</li>}
                        {(spot.available_days || spot.available_start_hour != null) && <li>· Host availability: {availabilitySummary}</li>}
                        <li>· Park only within your booked period — the property operator may enforce violations</li>
                        <li>· Access instructions unlock after payment authorization and must be followed</li>
                      </ul>
                      <label className="mt-3 flex items-start gap-2.5 cursor-pointer">
                        <input type="checkbox" checked={rulesAck} onChange={(e) => setRulesAck(e.target.checked)}
                               data-testid="booking-rules-ack" className="mt-0.5 w-4 h-4 accent-[#E50914]" />
                        <span className="text-xs text-zinc-300">I have read and agree to the rules for this booking, the <Link to="/terms" target="_blank" rel="noreferrer" className="text-[#FF3B45] underline underline-offset-2">Terms</Link> and the cancellation &amp; refund policy.</span>
                      </label>
                    </div>
                  )}

                  <button onClick={() => book()} disabled={busy || spot.paused || liveAvail === false || (!!user && !rangeValid) || (!!user && rangeValid && !rulesAck)} data-testid="book-this-spot-btn" className="btn-primary w-full mt-4 !h-12">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {user ? (quote ? `Continue to Payment · ${fmtMoney(quote.total_cents / 100, CUR)}` : "Continue to Payment") : "Log in to Book"}
                  </button>
                  {bookingError && (
                    <div className="mt-4 p-4 rounded-xl bg-red-500/10 border border-red-500/25 text-sm text-red-300" data-testid="booking-error" role="alert">
                      {bookingError}
                    </div>
                  )}
                  <p className="mt-4 text-xs text-zinc-600 text-center leading-relaxed">
                    Times snap to 15-minute increments. Your card is authorized, not charged — payment completes when your arrival code is verified. After authorization, cancellation refunds follow the tiered policy; service &amp; processing fees are non-refundable.
                  </p>
                </>
              ) : minimized ? null : (
                <div className="mt-7" data-testid="checkout-step">
                  {/* Server-authoritative 10-minute hold (Part 2): countdown is
                      presentation only, driven by hold_expires_at + server offset. */}
                  {holdLeft != null && (
                    <div className="mb-5 rounded-xl border border-[#FF3B45]/30 bg-[#FF3B45]/5 p-4 text-center" data-testid="hold-banner">
                      <p className="meta-label !text-[#FF3B45]/80">Spot held for you</p>
                      <p className="font-display font-bold text-2xl mt-1 tabular-nums" data-testid="hold-countdown">
                        {String(Math.floor(holdLeft / 60)).padStart(2, "0")}:{String(holdLeft % 60).padStart(2, "0")} remaining
                      </p>
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="type-h3">Payment authorization</h3>
                    <div className="flex items-center gap-3">
                      <StatusBadge value={booking.status} />
                      <button type="button" onClick={() => setMinimized(true)} data-testid="checkout-minimize-btn"
                              className="text-[11px] font-semibold text-zinc-500 hover:text-white transition-colors">
                        Minimize
                      </button>
                    </div>
                  </div>
                  <div className="mb-6 space-y-2 text-sm">
                    {/* Read-only final breakdown (PRD §7/§8) — the exact listing,
                        interval and confirmed pricing selection stay visible;
                        there is NO second selector here. */}
                    <div className="flex justify-between text-zinc-400"><span>Listing</span><span className="text-right truncate max-w-[60%]" data-testid="pay-listing">{booking.spot_title}</span></div>
                    <div className="flex justify-between text-zinc-400"><span>Interval</span><span data-testid="pay-interval" className="text-right">{fmtDateTimeZone(booking.start_time, booking.listing_timezone)} → {fmtDateTimeZone(booking.end_time, booking.listing_timezone)}</span></div>
                    <div className="flex justify-between text-zinc-400"><span>Rate plan</span><span data-testid="pay-rate-plan" className="capitalize">{booking.pricing_tier === "monthly" ? `Monthly · ${Math.round((booking.hours || 0) / 24)} day(s)` : `${booking.pricing_tier || "hourly"} · ${booking.hours}h`}</span></div>
                    <div className="flex justify-between text-zinc-400"><span>Base fare{booking.pricing_tier ? ` · ${booking.pricing_tier} rate` : ""}</span><span>{fmtMoney(booking.subtotal, CUR)}</span></div>
                    {(booking.pricing_periods || []).map((p, i) => (
                      <div key={i} className="flex justify-between text-zinc-500 pl-3 text-[13px]" data-testid={`pay-period-${i}`}><span>· {p.label}</span><span>{fmtMoney(p.parking_cents / 100, CUR)}</span></div>
                    ))}
                    {booking.discount_cents > 0 && (
                      <div className="flex justify-between text-emerald-400"><span>{booking.promotion_snapshot?.type === "coupon" ? `Promo ${booking.promotion_snapshot?.code || ""}`.trim() : booking.promotion_snapshot?.type === "referral_reward" ? "Referral discount" : "New-user reward"} ({booking.promotion_snapshot?.discount_percent}%)</span><span data-testid="booking-discount">-{fmtMoney(booking.discount_cents / 100, CUR)}</span></div>
                    )}
                    <div className="flex justify-between text-zinc-400"><span>Service fee</span><span data-testid="pay-platform-fee">{fmtMoney(booking.platform_fee + (booking.payment_handling_cents || 0) / 100, CUR)}</span></div>
                    <div className="flex justify-between text-zinc-400"><span>Taxes</span><span data-testid="pay-tax">{booking.tax_state === "calculated" ? fmtMoney(booking.tax_amount ?? (booking.tax_cents || 0) / 100, CUR) : booking.tax_state === "location_missing" ? "Unavailable — listing address incomplete" : "Calculated at confirmation"}</span></div>
                    <div className="flex justify-between font-display font-bold border-t border-[#1F2328] pt-2 mt-2"><span>Total</span><span data-testid="pay-total">{fmtMoney(booking.total, CUR)}</span></div>
                  </div>
                  <StripePayment
                    key={`${payment.client_secret}-${booking.total_cents || Math.round(payment.amount * 100)}`}
                    clientSecret={payment.client_secret}
                    publishableKey={payment.publishable_key}
                    amountLabel={fmtMoney(payment.amount)}
                    onSuccess={() => { toast.success("Card authorized — your arrival code is ready"); navigate(`/bookings/${booking.booking_id}`); }}
                    onError={(m) => toast.error(m)}
                  />
                  {canWalletPay && (
                    <button onClick={payWithWallet} disabled={busy} data-testid="pay-with-wallet-btn" className="btn-secondary w-full mt-3">
                      <WalletIcon className="w-4 h-4" /> Pay {fmtMoney(booking.total)} from wallet ({fmtCents(wallet.balance_cents)})
                    </button>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        </motion.div>

        {/* Minimized floating hold bar (Part 3) — lives outside the card flow */}
        <CheckoutHoldBar booking={minimized ? booking : null} spotTitle={spot.title} onContinue={() => setMinimized(false)} />

        {/* Add-vehicle modal — keeps the driver inside the booking flow: no page
            jump, booking state (date/time/pricing) is fully preserved, and the
            newly created vehicle is selected automatically. */}
        {vehicleModal && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
               role="dialog" aria-modal="true" aria-label="Add a vehicle" data-testid="booking-vehicle-modal">
            <div className="w-full max-w-lg rounded-2xl border border-[#27272A] bg-[#0D1114] p-6 shadow-2xl shadow-black/60 max-h-[85dvh] overflow-y-auto">
              <p className="font-display font-bold text-lg">Add a vehicle</p>
              <p className="mt-1 text-xs text-zinc-500">Saved vehicles can be reused on every booking. This one is selected automatically.</p>
              <div className="mt-5">
                <VehicleForm busy={vehBusy} setBusy={setVehBusy}
                             onSaved={(v) => { setVehicleModal(false); reloadVehicles(v?.vehicle_id); }}
                             onCancel={() => setVehicleModal(false)} />
              </div>
            </div>
          </div>
        )}

        {/* Driver declaration modal — required once per declaration version
            before the first booking; enforced again by the booking API. */}
        {consentOpen && (
          <DriverConsent
            onAccept={() => { setConsentOpen(false); setConsent({ accepted: true }); book(true); }}
            onDecline={() => { setConsentOpen(false); toast.error("Acceptance of the Driver declaration is required to book.", { duration: 5000 }); }}
          />
        )}

        {/* Persona identity verification — required by the server before any
            booking hold or payment is created. */}
        {personaOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
               role="dialog" aria-modal="true" aria-label="Identity verification" data-testid="persona-modal">
            <div className="w-full max-w-md rounded-2xl border border-[#27272A] bg-[#0D1114] p-6 shadow-2xl shadow-black/60 max-h-[85dvh] overflow-y-auto">
              <PersonaVerify onClose={() => setPersonaOpen(false)}
                             onVerified={() => { setPersonaOpen(false); toast.success("Identity verified — continuing your booking"); book(); }} />
            </div>
          </div>
        )}

        {/* Reviews — full width below the fold, both layouts */}
        <motion.div variants={fadeUp} initial="hidden" animate="show" className="mt-12" data-testid="spot-reviews-section">
          <p className="meta-label mb-4">Reviews</p>
          {reviews === null ? null : reviews.length === 0 ? (
            <p className="text-sm text-zinc-500" data-testid="reviews-empty">No reviews yet — this listing is new.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {reviews.map((r) => (
                <div key={r.review_id} className="surface-flat p-5" data-testid={`review-${r.review_id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-1 text-amber-400" aria-label={`${r.rating} out of 5 stars`}>
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? "fill-amber-400" : "text-zinc-700"}`} aria-hidden="true" />
                      ))}
                    </span>
                    <span className="text-[11px] text-zinc-600">{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                  {r.comment && <p className="mt-2.5 text-sm text-zinc-300 leading-relaxed">{r.comment}</p>}
                  <p className="mt-2 text-xs text-zinc-600">{r.reviewer_name || "Driver"}</p>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-7xl w-full mx-auto px-5 sm:px-8 pt-24 pb-16">{children}</main>
      <Footer />
    </div>
  );
}
